/**
 * IndexedDB-backed offline support.
 *
 * Two stores:
 *   - `pending-mutations`: queued writes (time entries, checklist saves
 *     and checklist creates) replayed when connectivity returns.
 *   - `cached-checklists`: checklists an engineer has opened while in
 *     signal, kept on the device so the sheet can be reopened and filled
 *     in with no connection at all.
 *
 * Queue entries may carry a `dedupeKey`: enqueuing replaces any existing
 * entry with the same key, so repeatedly saving one checklist collapses
 * to a single write carrying the latest payload instead of a pile of
 * stale ones.
 *
 * NOT in scope: caching arbitrary reads (Projects / Tasks), merge
 * conflict resolution (last write wins).
 */

const DB_NAME = 'expertfix-offline';
const DB_VERSION = 4;
const API_BASE = process.env.REACT_APP_BACKEND_URL;
const STORE = 'pending-mutations';
const CHECKLIST_STORE = 'cached-checklists';
const PHOTO_STORE = 'pending-photos';
// Generic on-device copies of any other document kind (test reports,
// damage reports, tasks, …) keyed `${kind}:${id}`.
export const DOC_STORE = 'cached-docs';

let _dbPromise = null;

function openDB() {
  if (_dbPromise) return _dbPromise;
  _dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'id', autoIncrement: true });
      }
      if (!db.objectStoreNames.contains(CHECKLIST_STORE)) {
        db.createObjectStore(CHECKLIST_STORE, { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains(PHOTO_STORE)) {
        db.createObjectStore(PHOTO_STORE, { keyPath: 'id', autoIncrement: true });
      }
      if (!db.objectStoreNames.contains(DOC_STORE)) {
        db.createObjectStore(DOC_STORE, { keyPath: 'key' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return _dbPromise;
}

async function tx(mode, storeName = STORE) {
  const db = await openDB();
  return db.transaction(storeName, mode).objectStore(storeName);
}

function notifyChanged() {
  try {
    window.dispatchEvent(new CustomEvent('offline-queue-changed'));
  } catch { /* SSR / non-browser */ }
}

const req2promise = (request) => new Promise((res, rej) => {
  request.onsuccess = () => res(request.result);
  request.onerror = () => rej(request.error);
});

export async function enqueue(entry) {
  // entry: { url, method, body, token, label, dedupeKey, createdAt }
  if (entry.dedupeKey) {
    const existing = await list();
    for (const item of existing) {
      if (item.dedupeKey === entry.dedupeKey) await _delete(item.id);
    }
  }
  const store = await tx('readwrite');
  await req2promise(store.add({ ...entry, createdAt: entry.createdAt || Date.now() }));
  notifyChanged();
}

export async function count() {
  const store = await tx('readonly');
  return req2promise(store.count());
}

export async function list() {
  const store = await tx('readonly');
  return (await req2promise(store.getAll())) || [];
}

async function _delete(id) {
  const store = await tx('readwrite');
  return req2promise(store.delete(id));
}

async function _get(id) {
  const store = await tx('readonly');
  return req2promise(store.get(id));
}

async function _patch(id, patch) {
  const store = await tx('readwrite');
  const existing = await req2promise(store.get(id));
  if (!existing) return;
  const store2 = await tx('readwrite');
  return req2promise(store2.put({ ...existing, ...patch }));
}

/**
 * Attempt to replay every queued item back to the server.
 * Stops on the first hard failure so we don't hammer a broken backend.
 * Returns { flushed, remaining }.
 *
 * Re-entrancy matters: the `online` event and the boot timer can both
 * fire within milliseconds of each other, and two concurrent passes
 * would each POST the same queued create — the server would end up with
 * two records. So a single pass runs at a time and a request that
 * arrives mid-pass is coalesced into one extra pass afterwards.
 */
let _drainInFlight = null;
let _drainAgain = false;

export async function drainQueue() {
  if (_drainInFlight) {
    _drainAgain = true;
    return _drainInFlight;
  }
  _drainInFlight = _drainOnce();
  try {
    return await _drainInFlight;
  } finally {
    _drainInFlight = null;
    if (_drainAgain) {
      _drainAgain = false;
      drainQueue();
    }
  }
}

async function _drainOnce() {
  if (!navigator.onLine) return { flushed: 0, remaining: await count() };
  const items = await list();
  let flushed = 0;
  let conflicts = 0;
  for (const queued of items) {
    // Re-read: an earlier flush in this same pass may have rewritten
    // this entry's URL (offline-created checklist getting its real id).
    const item = (await _get(queued.id)) || queued;
    // A conflict needs the user to choose — skip it, don't block the rest.
    if (item.conflict) { conflicts += 1; continue; }
    try {
      const res = await fetch(item.url, {
        method: item.method || 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(item.token ? { Authorization: `Bearer ${item.token}` } : {}),
        },
        body: JSON.stringify(item.body || {}),
      });
      if (res.ok) {
        // A checklist created offline held a temporary id; photos taken
        // against it must be re-pointed at the real one before upload.
        if (item.localChecklistId || item.localDocId) {
          const created = await res.json().catch(() => null);
          const oldId = item.localChecklistId || item.localDocId;
          if (created?.id) {
            await remapPendingPhotos(oldId, created.id);
            // Later writes against the same document (offline sign-offs,
            // follow-up saves) still point at the temporary id.
            await remapPendingMutations(oldId, created.id);
            // Some create endpoints only accept a subset of the document
            // (a test report seeds its own blank rows, for example), so
            // the offline-filled content follows as an immediate update.
            if (item.postCreateBody) {
              const followUp = `${item.url}/${created.id}`;
              try {
                const up = await fetch(followUp, {
                  method: 'PUT',
                  headers: {
                    'Content-Type': 'application/json',
                    ...(item.token ? { Authorization: `Bearer ${item.token}` } : {}),
                  },
                  body: JSON.stringify(item.postCreateBody),
                });
                if (!up.ok) throw new Error(String(up.status));
              } catch {
                // Retry as its own queue entry rather than lose the work.
                await enqueue({
                  url: followUp, method: 'PUT', body: item.postCreateBody,
                  token: item.token, dedupeKey: `${item.dedupeKey || 'doc'}:content`,
                  label: item.label,
                });
              }
            }
          }
          if (item.localChecklistId) {
            try { await removeCachedChecklist(oldId); } catch { /* best effort */ }
          }
          if (item.localDocKind) {
            try { await removeCachedDocRaw(item.localDocKind, oldId); } catch { /* best effort */ }
          }
          try {
            window.dispatchEvent(new CustomEvent('offline-doc-synced', {
              detail: { kind: item.localDocKind || 'checklist', oldId, newId: created?.id },
            }));
          } catch { /* non-browser */ }
        }
        await _delete(item.id);
        flushed += 1;
      } else if (res.status === 409) {
        // Someone else changed the record while we were offline. Keep the
        // work and flag it so the user can decide.
        const detail = await res.json().catch(() => ({}));
        await _patch(item.id, { conflict: detail?.detail || true, conflictAt: Date.now() });
        conflicts += 1;
      } else if (res.status >= 400 && res.status < 500 && res.status !== 401 && res.status !== 408 && res.status !== 429) {
        // Client-side validation error — replaying will never work.
        // Drop the entry so it doesn't block the queue forever. The
        // user has already seen the toast when they logged the hours.
        await _delete(item.id);
        flushed += 1;
      } else {
        // 401 / 5xx / network hiccup — bail out and retry later.
        break;
      }
    } catch {
      break;
    }
  }
  const photos = await drainPhotos();
  notifyChanged();
  if (conflicts > 0) {
    // Auto-drain (the `online` event) has no caller to report to, so
    // announce conflicts on the bus for the badge to pick up.
    try {
      window.dispatchEvent(new CustomEvent('offline-sync-conflicts', { detail: { conflicts } }));
    } catch { /* non-browser */ }
  }
  return { flushed, photos, conflicts, remaining: await count() };
}


// ---- Pending photos ----------------------------------------------------
// Photos are binary, so they live in their own store as Blobs and upload
// as multipart once there's signal.

export async function queuePhoto({ entityType, entityId, itemKey, file, token }) {
  const store = await tx('readwrite', PHOTO_STORE);
  await req2promise(store.add({
    entityType,
    entityId,
    itemKey: itemKey == null ? null : String(itemKey),
    blob: file,
    name: file.name || 'photo.jpg',
    type: file.type || 'image/jpeg',
    token,
    createdAt: Date.now(),
  }));
  notifyChanged();
}

export async function listPendingPhotos(entityType = null, entityId = null, itemKey = undefined) {
  const store = await tx('readonly', PHOTO_STORE);
  const all = (await req2promise(store.getAll())) || [];
  return all.filter((p) => {
    if (entityType && p.entityType !== entityType) return false;
    if (entityId && p.entityId !== entityId) return false;
    if (itemKey !== undefined && p.itemKey !== (itemKey == null ? null : String(itemKey))) return false;
    return true;
  });
}

export async function countPendingPhotos() {
  const store = await tx('readonly', PHOTO_STORE);
  return req2promise(store.count());
}

export async function removePendingPhoto(id) {
  const store = await tx('readwrite', PHOTO_STORE);
  await req2promise(store.delete(id));
  notifyChanged();
}

async function remapPendingPhotos(oldEntityId, newEntityId) {
  const pending = await listPendingPhotos(null, oldEntityId);
  for (const p of pending) {
    const store = await tx('readwrite', PHOTO_STORE);
    await req2promise(store.put({ ...p, entityId: newEntityId }));
  }
}

/**
 * Queued writes aimed at a checklist that was created offline carry its
 * temporary `local-<uuid>` id in the URL (and in `dedupeKey`). Once the
 * server issues the real id, re-point them so they don't 404.
 */
async function remapPendingMutations(oldId, newId) {
  const items = await list();
  for (const it of items) {
    if (!it.url || !String(it.url).includes(oldId)) continue;
    await _patch(it.id, {
      url: String(it.url).split(oldId).join(newId),
      dedupeKey: it.dedupeKey ? String(it.dedupeKey).split(oldId).join(newId) : it.dedupeKey,
    });
  }
}


async function drainPhotos() {
  if (!navigator.onLine) return 0;
  const pending = await listPendingPhotos();
  let sent = 0;
  for (const p of pending) {
    // Still pinned to a checklist that hasn't been created yet.
    if (isLocalId(p.entityId)) continue;
    try {
      const fd = new FormData();
      fd.append('file', p.blob, p.name);
      if (p.itemKey != null) fd.append('item_key', p.itemKey);
      const res = await fetch(`${API_BASE}/api/entity-photos/${p.entityType}/${p.entityId}`, {
        method: 'POST',
        headers: p.token ? { Authorization: `Bearer ${p.token}` } : {},
        body: fd,
      });
      if (res.ok) { await removePendingPhoto(p.id); sent += 1; }
      else if (res.status >= 400 && res.status < 500 && res.status !== 401) {
        // Rejected outright (too big, bad type) — dropping beats retrying forever.
        await removePendingPhoto(p.id);
      } else break;
    } catch { break; }
  }
  return sent;
}


// ---- Conflicts ---------------------------------------------------------

export async function listConflicts() {
  return (await list()).filter((i) => i.conflict);
}

/** Re-send a conflicted write, overwriting whatever is on the server. */
export async function resolveConflictKeepMine(entryId) {
  const entry = (await list()).find((i) => i.id === entryId);
  if (!entry) return;
  await _patch(entryId, { conflict: null, body: { ...entry.body, force: true } });
  return drainQueue();
}

/** Throw away the offline edit and keep whatever the server has. */
export async function resolveConflictKeepTheirs(entryId) {
  const entry = (await list()).find((i) => i.id === entryId);
  if (entry?.localChecklistId) {
    await removeCachedChecklist(entry.localChecklistId).catch(() => {});
  }
  if (entry?.localDocId && entry?.localDocKind) {
    await removeCachedDocRaw(entry.localDocKind, entry.localDocId).catch(() => {});
  }
  await _delete(entryId);
  notifyChanged();
}

/**
 * Upload a hand-merged body (some fields mine, some the server's).
 * Sent with `force` so the server accepts it despite the stale base.
 */
export async function resolveConflictMerge(entryId, mergedBody) {
  const entry = (await list()).find((i) => i.id === entryId);
  if (!entry) return;
  await _patch(entryId, { conflict: null, body: { ...mergedBody, force: true } });
  return drainQueue();
}


// ---- Cached documents (generic) ---------------------------------------
// Any document kind other than checklists lives here, keyed
// `${kind}:${id}`, so a new kind needs no schema change.

const docKey = (kind, id) => `${kind}:${id}`;

export async function cacheDocRaw(kind, doc, template = null) {
  if (!doc?.id) return;
  const key = docKey(kind, doc.id);
  const prev = await getCachedDocRaw(kind, doc.id);
  const store = await tx('readwrite', DOC_STORE);
  await req2promise(store.put({
    key,
    kind,
    id: doc.id,
    project_id: doc.project_id ?? prev?.project_id ?? null,
    doc: { ...(prev?.doc || {}), ...doc },
    template: template || prev?.template || null,
    cachedAt: Date.now(),
  }));
  notifyChanged();
}

export async function getCachedDocRaw(kind, id) {
  const store = await tx('readonly', DOC_STORE);
  return req2promise(store.get(docKey(kind, id)));
}

export async function listCachedDocsRaw(kind, projectId = null) {
  const store = await tx('readonly', DOC_STORE);
  const all = (await req2promise(store.getAll())) || [];
  return all
    .filter((r) => r.kind === kind && (!projectId || r.project_id === projectId))
    .sort((a, b) => (b.cachedAt || 0) - (a.cachedAt || 0));
}

export async function removeCachedDocRaw(kind, id) {
  const store = await tx('readwrite', DOC_STORE);
  await req2promise(store.delete(docKey(kind, id)));
  notifyChanged();
}


// ---- Cached checklists -------------------------------------------------
// An engineer can only work offline on sheets already on the device, so
// every checklist opened while in signal is stored here automatically.

export const LOCAL_ID_PREFIX = 'local-';
export const isLocalId = (id) => String(id || '').startsWith(LOCAL_ID_PREFIX);

export async function cacheChecklist(instance, template) {
  if (!instance?.id) return;
  const store = await tx('readwrite', CHECKLIST_STORE);
  await req2promise(store.put({
    id: instance.id,
    project_id: instance.project_id,
    instance,
    template,
    cachedAt: Date.now(),
  }));
  notifyChanged();
}

export async function getCachedChecklist(id) {
  const store = await tx('readonly', CHECKLIST_STORE);
  return req2promise(store.get(id));
}

export async function listCachedChecklists(projectId = null) {
  const store = await tx('readonly', CHECKLIST_STORE);
  const all = (await req2promise(store.getAll())) || [];
  const rows = projectId ? all.filter((r) => r.project_id === projectId) : all;
  return rows.sort((a, b) => (b.cachedAt || 0) - (a.cachedAt || 0));
}

export async function removeCachedChecklist(id) {
  const store = await tx('readwrite', CHECKLIST_STORE);
  await req2promise(store.delete(id));
  notifyChanged();
}

// The template list is small JSON and needed to start a fresh sheet
// offline, so localStorage is plenty.
const TEMPLATES_KEY = 'expertfix-checklist-templates';

export function cacheTemplates(templates) {
  try {
    localStorage.setItem(TEMPLATES_KEY, JSON.stringify(templates || []));
  } catch { /* quota / private mode */ }
}

export function getCachedTemplates() {
  try {
    return JSON.parse(localStorage.getItem(TEMPLATES_KEY) || '[]');
  } catch {
    return [];
  }
}

// Wire the `online` event once at module load. Multiple imports get the
// same listener because `_wired` is module-scoped.
let _wired = false;
export function attachAutoDrain() {
  if (_wired || typeof window === 'undefined') return;
  _wired = true;
  window.addEventListener('online', () => { drainQueue(); });
  // Also flush on a fresh tab boot in case we come back with items
  // queued from a previous session.
  if (navigator.onLine) {
    setTimeout(() => { drainQueue(); }, 500);
  }
}
